/**
 * DocumentEditorPage
 *
 * View, edit, export, and manage generated documents.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/dashboard';
import { MarkdownRenderer, CitationReportPanel } from '../components/casemaker';
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Save,
  Send,
  Trash2,
  Copy,
  Check,
  FileText,
  Edit3,
  Eye,
  MoreVertical,
  AlertCircle,
  AlertTriangle,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import { casemakerService } from '../services/casemakerService';
import { useGeneratedDocExport } from '../hooks/useGeneratedDocExport';
import {
  GeneratedDocument,
  ExportFormat,
  ExportArtifact,
  SubmissionMethod,
  STATUS_STYLES,
  CATEGORY_STYLES,
  getDocumentTypeInfo,
  formatTimestamp,
  formatDateTime,
} from '../types/generatedDoc';

// Regeneration progress messages
const REGENERATION_PROGRESS_MESSAGES = [
  { delay: 0, message: 'Starting regeneration...' },
  { delay: 3000, message: 'Analyzing context...' },
  { delay: 10000, message: 'Generating new content...' },
  { delay: 25000, message: 'Formatting document...' },
  { delay: 40000, message: 'Finalizing...' },
];

type ViewMode = 'preview' | 'edit';
type ContentTab = 'document' | 'citations';

export function DocumentEditorPage(): JSX.Element {
  const { caseFileId, docId } = useParams<{ caseFileId: string; docId: string }>();
  const navigate = useNavigate();

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('preview');
  const [contentTab, setContentTab] = useState<ContentTab>('document');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Document state
  const [document, setDocument] = useState<GeneratedDocument | null>(null);
  const [caseFileName, setCaseFileName] = useState<string>('');
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');

  // Submit form state
  const [submissionMethod, setSubmissionMethod] = useState<SubmissionMethod>('email');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');

  // Progress tracking for regeneration
  const [regenerationProgress, setRegenerationProgress] = useState('');
  const progressTimersRef = useRef<NodeJS.Timeout[]>([]);

  // Clear progress timers
  const clearProgressTimers = useCallback(() => {
    progressTimersRef.current.forEach((timer) => clearTimeout(timer));
    progressTimersRef.current = [];
    setRegenerationProgress('');
  }, []);

  // Start regeneration progress messages
  const startRegenerationProgress = useCallback(() => {
    clearProgressTimers();
    REGENERATION_PROGRESS_MESSAGES.forEach(({ delay, message }) => {
      const timer = setTimeout(() => {
        setRegenerationProgress(message);
      }, delay);
      progressTimersRef.current.push(timer);
    });
  }, [clearProgressTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearProgressTimers();
    };
  }, [clearProgressTimers]);

  // Parse error messages for user-friendly display
  const parseErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      if (message.includes('timeout') || message.includes('timed out')) {
        return 'The request took too long. Please try again.';
      }
      if (message.includes('network') || message.includes('fetch')) {
        return 'Unable to connect to the server. Please check your internet connection.';
      }
      if (message.includes('unauthorized') || message.includes('401')) {
        return 'Your session has expired. Please refresh the page and log in again.';
      }
      if (error.message.length < 150) {
        return error.message;
      }
      return 'An unexpected error occurred. Please try again.';
    }
    return 'An error occurred. Please try again.';
  };

  const loadDocument = useCallback(async () => {
    if (!docId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await casemakerService.getDocument({ docId });

      if (response.success && response.document) {
        setDocument(response.document);
        setCaseFileName(response.caseFileName || '');
        setEditedContent(response.document.editedContent || response.document.generatedContent);
        setEditedTitle(response.document.title);
      } else {
        setError(response.error || 'Failed to load document. Please try again.');
      }
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  const handleSave = async () => {
    if (!docId || !document) return;

    setIsSaving(true);
    setError(null);

    try {
      const response = await casemakerService.updateDocument({
        docId,
        editedContent,
        title: editedTitle !== document.title ? editedTitle : undefined,
      });

      if (response.success && response.document) {
        setDocument(response.document);
        setSuccessMessage('Document saved successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        setViewMode('preview');
      } else {
        setError(response.error || 'Failed to save document. Please try again.');
      }
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  // IMPL-002: the export menu no longer exports directly — it opens the
  // Export Review modal, which owns the acknowledgment + gate pipeline
  // (server-enforced; see ExportReviewModal). isExporting mirrors the modal
  // being open so the Export button reflects the in-progress review.
  const exportReview = useGeneratedDocExport({
    onExported: ({ warning }) => {
      setSuccessMessage(
        warning ? `Document exported. ${warning}` : 'Document exported successfully'
      );
      setTimeout(() => setSuccessMessage(null), 3000);
      // Refresh document to show export record + status change
      loadDocument();
    },
    // PR-3 (finding 1): the doc changed after the review snapshot — refresh
    // so the next export-menu open reviews the latest version.
    onDocumentChanged: () => {
      loadDocument();
    },
  });

  const isExporting = exportReview.state.isOpen;

  const handleExport = (format: ExportFormat, artifact: ExportArtifact = 'letter') => {
    if (!document) return;
    setShowExportMenu(false);
    setError(null);
    exportReview.open({ document, format, artifact });
  };

  const handleRegenerate = async () => {
    if (!docId) return;

    setIsRegenerating(true);
    setError(null);
    startRegenerationProgress();

    try {
      const response = await casemakerService.regenerateDocument({ docId });

      clearProgressTimers();

      if (response.success && response.generatedContent) {
        setSuccessMessage('Document regenerated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        loadDocument();
      } else {
        setError(response.error || 'Failed to regenerate document. Please try again.');
      }
    } catch (err) {
      clearProgressTimers();
      setError(parseErrorMessage(err));
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!docId || !caseFileId) return;

    setIsDeleting(true);
    setError(null);

    try {
      const response = await casemakerService.deleteDocument({ docId });

      if (response.success) {
        navigate(`/casefile/${caseFileId}/casemaker`);
      } else {
        setError(response.error || 'Failed to delete document. Please try again.');
      }
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleMarkSubmitted = async () => {
    if (!docId) return;

    setIsSaving(true);
    setError(null);

    try {
      const response = await casemakerService.updateDocument({
        docId,
        status: 'submitted',
        submissionMethod,
        submissionNotes: submissionNotes || undefined,
        confirmationNumber: confirmationNumber || undefined,
      });

      if (response.success && response.document) {
        setDocument(response.document);
        setSuccessMessage('Document marked as submitted');
        setTimeout(() => setSuccessMessage(null), 3000);
        setShowSubmitModal(false);
      } else {
        setError(response.error || 'Failed to update document. Please try again.');
      }
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyContent = () => {
    if (!document) return;
    const content = document.editedContent || document.generatedContent;
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBackToCaseMaker = () => {
    navigate(`/casefile/${caseFileId}/casemaker`);
  };

  if (!caseFileId || !docId) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <p className="text-muted">Invalid document ID</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse">
            <FileText className="mx-auto mb-4 text-muted" size={48} />
            <p className="text-muted">Loading document...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="text-center">
          <FileText className="mx-auto mb-4 text-muted" size={48} />
          <p className="text-muted mb-4">Document not found</p>
          <button onClick={handleBackToCaseMaker} className="btn-primary">
            Back to CaseMaker
          </button>
        </div>
      </div>
    );
  }

  const typeInfo = getDocumentTypeInfo(document.documentType);
  const statusStyle = STATUS_STYLES[document.status];
  const categoryStyle = typeInfo ? CATEGORY_STYLES[typeInfo.category] : null;
  const displayContent = document.editedContent || document.generatedContent;
  const createdDate = formatTimestamp(document.createdAt);
  const updatedDate = formatTimestamp(document.updatedAt);

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
      </div>

      {/* Sidebar - Mobile Overlay */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-ink/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar className="h-full" activeCaseFileId={caseFileId} />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-soft-neutral sticky top-0 z-10">
          <div className="px-4 lg:px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={handleBackToCaseMaker}
                  className="btn-ghost btn-sm p-1 flex-shrink-0"
                  aria-label="Back to CaseMaker"
                >
                  <ArrowLeft size={20} aria-hidden="true" />
                </button>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {categoryStyle && (
                      <span className={`badge ${categoryStyle.bg} ${categoryStyle.text} badge-sm`}>
                        {categoryStyle.label}
                      </span>
                    )}
                    <span className={`badge ${statusStyle.bg} ${statusStyle.text} badge-sm`}>
                      {statusStyle.label}
                    </span>
                    {document.hasBeenEdited && (
                      <span className="badge bg-yellow-100 text-yellow-700 badge-sm">Edited</span>
                    )}
                  </div>
                  <h1 className="text-lg font-semibold text-ink truncate mt-1">
                    {document.title}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* View/Edit Toggle — hidden on Citations tab (read-only) */}
                {contentTab === 'document' && (
                  <div className="hidden sm:flex items-center bg-soft-neutral rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('preview')}
                      className={`px-3 py-1 rounded text-sm ${
                        viewMode === 'preview'
                          ? 'bg-white text-ink shadow-sm'
                          : 'text-muted hover:text-ink'
                      }`}
                    >
                      <Eye size={16} className="inline mr-1" />
                      Preview
                    </button>
                    <button
                      onClick={() => setViewMode('edit')}
                      className={`px-3 py-1 rounded text-sm ${
                        viewMode === 'edit'
                          ? 'bg-white text-ink shadow-sm'
                          : 'text-muted hover:text-ink'
                      }`}
                    >
                      <Edit3 size={16} className="inline mr-1" />
                      Edit
                    </button>
                  </div>
                )}

                {/* Actions */}
                {contentTab === 'document' && viewMode === 'edit' ? (
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save size={18} />
                    <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
                  </button>
                ) : (
                  <>
                    {/* Export Dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => !isExporting && setShowExportMenu(!showExportMenu)}
                        disabled={isExporting}
                        className="btn-primary flex items-center gap-2"
                      >
                        {isExporting ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Download size={18} />
                        )}
                        <span className="hidden sm:inline">
                          {isExporting ? 'Exporting...' : 'Export'}
                        </span>
                      </button>
                      {showExportMenu && !isExporting && (
                        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-soft-neutral z-20 p-2">
                          <ExportMenuSection
                            icon={<FileText size={14} className="text-muted" />}
                            label="Letter"
                            hint="The recipient-facing document"
                            onPick={(format) => handleExport(format, 'letter')}
                            disabled={false}
                          />
                          <ExportMenuSection
                            icon={<ShieldCheck size={14} className="text-muted" />}
                            label="Source Citations"
                            hint={
                              document.citationReport
                                ? 'The "show your work" report'
                                : 'No citations available for this document'
                            }
                            onPick={(format) => handleExport(format, 'citations')}
                            disabled={!document.citationReport}
                          />
                          <ExportMenuSection
                            icon={<FileText size={14} className="text-muted" />}
                            label="Letter + Citations bundle"
                            hint={
                              document.citationReport
                                ? 'Both in one file'
                                : 'No citations available for this document'
                            }
                            onPick={(format) => handleExport(format, 'bundle')}
                            disabled={!document.citationReport}
                          />
                        </div>
                      )}
                    </div>

                    {/* More Actions */}
                    <div className="relative">
                      <button
                        onClick={() => {}}
                        className="btn-ghost p-2"
                        aria-label="More actions"
                      >
                        <MoreVertical size={18} aria-hidden="true" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Messages */}
        {error && (
          <div className="mx-4 lg:mx-6 mt-4">
            <div className="card border-red-200 bg-red-50">
              <div className="card-body py-3 flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="flex-1">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mx-4 lg:mx-6 mt-4">
            <div className="card border-green-200 bg-green-50">
              <div className="card-body py-3 flex items-center gap-3">
                <Check className="text-green-600 flex-shrink-0" size={18} />
                <p className="text-green-700 text-sm flex-1">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Stale citations banner — surface on either tab so caregivers
            see it whether they're reading the letter or reviewing citations */}
        {document.citationReport?.stale && (
          <div className="mx-4 lg:mx-6 mt-4">
            <div className="card border-amber-200 bg-amber-50">
              <div className="card-body py-3 flex items-start gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Citations may be outdated
                  </p>
                  <p className="text-sm text-amber-700 mt-0.5">
                    The letter has been edited since these citations were generated.
                    Regenerate to refresh.
                  </p>
                </div>
                <button
                  onClick={handleRegenerate}
                  disabled={isRegenerating}
                  className="text-amber-700 hover:text-amber-900 text-sm font-medium underline"
                >
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div id="main-content" role="main" tabIndex={-1} className="flex-1 p-4 lg:p-6 outline-none">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {/* Tab toggle — only render when citations exist */}
                {document.citationReport && (
                  <div
                    className="flex items-center border-b border-soft-neutral mb-4"
                    role="tablist"
                    aria-label="Document views"
                  >
                    <button
                      role="tab"
                      aria-selected={contentTab === 'document'}
                      onClick={() => setContentTab('document')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        contentTab === 'document'
                          ? 'border-evergreen text-ink'
                          : 'border-transparent text-muted hover:text-ink'
                      }`}
                    >
                      <FileText size={16} className="inline mr-1.5 -mt-0.5" />
                      Document
                    </button>
                    <button
                      role="tab"
                      aria-selected={contentTab === 'citations'}
                      onClick={() => setContentTab('citations')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                        contentTab === 'citations'
                          ? 'border-evergreen text-ink'
                          : 'border-transparent text-muted hover:text-ink'
                      }`}
                    >
                      <ShieldCheck size={16} className="-mt-0.5" />
                      Source Citations
                      <span className="ml-1 inline-flex items-center justify-center text-xs bg-soft-neutral text-ink rounded-full px-1.5 py-0.5 min-w-[1.25rem]">
                        {document.citationReport.summary.totalCount}
                      </span>
                    </button>
                  </div>
                )}

                {contentTab === 'citations' && document.citationReport ? (
                  <CitationReportPanel
                    report={document.citationReport}
                    caseFileId={caseFileId}
                  />
                ) : viewMode === 'edit' ? (
                  <div className="card">
                    <div className="card-header">
                      <label className="label">Document Title</label>
                      <input
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    <div className="card-body">
                      <label className="label">Content</label>
                      <textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="textarea w-full font-mono text-sm"
                        style={{ minHeight: '500px' }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="card-header flex items-center justify-between">
                      <h2 className="font-semibold text-ink">Document Content</h2>
                      <button
                        onClick={handleCopyContent}
                        className="btn-ghost btn-sm flex items-center gap-1"
                      >
                        {copied ? (
                          <>
                            <Check size={16} className="text-green-600" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy size={16} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <div className="card-body">
                      <MarkdownRenderer content={displayContent} />
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                {/* Document Info */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="font-medium text-ink">Document Info</h3>
                  </div>
                  <div className="card-body space-y-3">
                    <div>
                      <p className="text-xs text-muted">Type</p>
                      <p className="text-sm font-medium text-ink">
                        {typeInfo?.name || document.documentType}
                      </p>
                    </div>
                    {caseFileName && (
                      <div>
                        <p className="text-xs text-muted">CaseFile</p>
                        <p className="text-sm font-medium text-ink">{caseFileName}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted">Created</p>
                      <p className="text-sm text-ink">{formatDateTime(createdDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted">Last Updated</p>
                      <p className="text-sm text-ink">{formatDateTime(updatedDate)}</p>
                    </div>
                    {document.modelUsed && (
                      <div>
                        <p className="text-xs text-muted">AI Model</p>
                        <p className="text-sm text-ink">{document.modelUsed}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="font-medium text-ink">Actions</h3>
                  </div>
                  <div className="card-body space-y-2">
                    <button
                      onClick={() => {
                        // If on Citations tab, jump back to Document with the
                        // opposite mode of where we last were.
                        if (contentTab !== 'document') setContentTab('document');
                        setViewMode(viewMode === 'edit' ? 'preview' : 'edit');
                      }}
                      className="btn-ghost w-full justify-start flex items-center gap-2"
                      disabled={isRegenerating}
                    >
                      {viewMode === 'edit' ? <Eye size={18} /> : <Edit3 size={18} />}
                      {viewMode === 'edit' ? 'Preview' : 'Edit'}
                    </button>
                    {isRegenerating ? (
                      <div className="py-3 px-2 bg-sage/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Loader2 size={18} className="text-evergreen animate-spin" />
                          <span className="text-sm font-medium text-ink">
                            {regenerationProgress || 'Starting...'}
                          </span>
                        </div>
                        <div className="h-1.5 bg-soft-neutral rounded-full overflow-hidden">
                          <div className="h-full bg-evergreen rounded-full animate-progress-indeterminate" />
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={handleRegenerate}
                        className="btn-ghost w-full justify-start flex items-center gap-2"
                      >
                        <RefreshCw size={18} />
                        Regenerate
                      </button>
                    )}
                    {document.status !== 'submitted' && (
                      <button
                        onClick={() => setShowSubmitModal(true)}
                        className="btn-ghost w-full justify-start flex items-center gap-2 text-teal-600"
                      >
                        <Send size={18} />
                        Mark as Submitted
                      </button>
                    )}
                    <div className="border-t border-soft-neutral pt-2 mt-2">
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="btn-ghost w-full justify-start flex items-center gap-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={18} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>

                {/* Recipient Info */}
                {document.recipientInfo && Object.keys(document.recipientInfo).length > 0 && (
                  <div className="card">
                    <div className="card-header">
                      <h3 className="font-medium text-ink">Recipient</h3>
                    </div>
                    <div className="card-body space-y-2">
                      {document.recipientInfo.name && (
                        <p className="text-sm font-medium text-ink">
                          {document.recipientInfo.name}
                          {document.recipientInfo.title && `, ${document.recipientInfo.title}`}
                        </p>
                      )}
                      {document.recipientInfo.organization && (
                        <p className="text-sm text-muted">{document.recipientInfo.organization}</p>
                      )}
                      {document.recipientInfo.address && (
                        <p className="text-sm text-muted whitespace-pre-line">
                          {document.recipientInfo.address}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Submission Info */}
                {document.status === 'submitted' && (
                  <div className="card border-teal-200 bg-teal-50">
                    <div className="card-header">
                      <h3 className="font-medium text-teal-800">Submitted</h3>
                    </div>
                    <div className="card-body space-y-2">
                      {document.submissionMethod && (
                        <div>
                          <p className="text-xs text-teal-600">Method</p>
                          <p className="text-sm text-teal-800 capitalize">
                            {document.submissionMethod.replace('_', ' ')}
                          </p>
                        </div>
                      )}
                      {document.confirmationNumber && (
                        <div>
                          <p className="text-xs text-teal-600">Confirmation #</p>
                          <p className="text-sm text-teal-800">{document.confirmationNumber}</p>
                        </div>
                      )}
                      {document.submissionNotes && (
                        <div>
                          <p className="text-xs text-teal-600">Notes</p>
                          <p className="text-sm text-teal-800">{document.submissionNotes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export Review Modal (IMPL-002 — Layer 4 acknowledgment gate) */}
      <exportReview.Modal />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4">
          <div className="card w-full max-w-md">
            <div className="card-header">
              <h3 className="font-semibold text-ink">Delete Document?</h3>
            </div>
            <div className="card-body">
              <p className="text-muted">
                Are you sure you want to delete "{document.title}"? This action cannot be undone.
              </p>
            </div>
            <div className="card-footer flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn-primary bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4">
          <div className="card w-full max-w-md">
            <div className="card-header">
              <h3 className="font-semibold text-ink">Mark as Submitted</h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="label">Submission Method</label>
                <select
                  value={submissionMethod}
                  onChange={(e) => setSubmissionMethod(e.target.value as SubmissionMethod)}
                  className="select w-full"
                >
                  <option value="email">Email</option>
                  <option value="mail">Mail</option>
                  <option value="fax">Fax</option>
                  <option value="portal">Online Portal</option>
                  <option value="in_person">In Person</option>
                </select>
              </div>
              <div>
                <label className="label">Confirmation Number (optional)</label>
                <input
                  type="text"
                  value={confirmationNumber}
                  onChange={(e) => setConfirmationNumber(e.target.value)}
                  placeholder="e.g., REF-12345"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="label">Notes (optional)</label>
                <textarea
                  value={submissionNotes}
                  onChange={(e) => setSubmissionNotes(e.target.value)}
                  placeholder="Any additional notes about the submission..."
                  className="textarea w-full"
                />
              </div>
            </div>
            <div className="card-footer flex justify-end gap-3">
              <button
                onClick={() => setShowSubmitModal(false)}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkSubmitted}
                disabled={isSaving}
                className="btn-primary"
              >
                {isSaving ? 'Saving...' : 'Mark Submitted'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Two-format row inside the export dropdown. Renders an artifact label
 * with two side-by-side format buttons (PDF / DOCX). Single source of
 * truth for the export-menu visual; disabled state greys both buttons
 * and the hint string.
 */
interface ExportMenuSectionProps {
  icon: JSX.Element;
  label: string;
  hint: string;
  onPick: (format: ExportFormat) => void;
  disabled: boolean;
}

function ExportMenuSection({
  icon,
  label,
  hint,
  onPick,
  disabled,
}: ExportMenuSectionProps): JSX.Element {
  return (
    <div className={`px-2 py-2 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-medium text-ink">{label}</span>
      </div>
      <p className="text-xs text-muted ml-6 mb-2">{hint}</p>
      <div className="ml-6 flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onPick('pdf')}
          className="text-xs px-2 py-1 rounded border border-soft-neutral hover:bg-sage/30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          PDF
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onPick('docx')}
          className="text-xs px-2 py-1 rounded border border-soft-neutral hover:bg-sage/30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          DOCX
        </button>
      </div>
    </div>
  );
}

export default DocumentEditorPage;
